import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Download, Shield, ScrollText, ArrowUpRight, Link as LinkIcon, BookOpen } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Navbar, Footer } from '@/components/Layout';

const CATEGORIES = [
  {
    id: 'onchain-proof',
    title: 'On-Chain Proof',
    description: 'Live transaction receipts from the protocol running on a public testnet: every claim backed by a tx hash.',
    icon: LinkIcon,
    accent: 'from-red-500/10 to-orange-500/10',
    border: 'border-red-500/20',
    iconTint: 'text-red-400',
    items: [
      {
        name: 'Interactive On-Chain Receipts Page (live)',
        version: 'Reads Sepolia state at every page load',
        description: 'Visit /on-chain-receipts to see live contract wiring, slash receipt state, and a free-text agent lookup that queries Base Sepolia directly from your browser. Every number is read at page-load time: no caching, no marketing varnish.',
        file: '/on-chain-receipts',
        pages: 'Live page',
      },
      {
        name: 'Live Slash Proof: Kojiru Agent Credit Protocol',
        version: 'Base Sepolia · 6-28-2026',
        description: 'End-to-end on-chain receipts: agent registered, 200 mKOJI collateral posted, $1 of debt slashed, exactly 100 mKOJI routed to the test counterparty on Base Sepolia (tx 0x694ffe5f…ef1d276). Includes runtime bytecode hashes for independent verification by any reviewer. Regenerable from a public RPC at build time.',
        file: '/kojiru-slash-proof-onepager.pdf',
        pages: 'BD · DD-ready',
      },
    ],
  },
  {
    id: 'protocol',
    title: 'Protocol & Whitepapers',
    description: 'Core documentation of the Kojiru Agent Credit Protocol.',
    icon: ScrollText,
    accent: 'from-blue-500/10 to-purple-500/10',
    border: 'border-blue-500/20',
    iconTint: 'text-blue-400',
    items: [
      {
        name: 'Agent Credit Protocol Whitepaper',
        version: 'v2.0',
        description: '15-section technical deep-dive covering identity, reputation, scoring, credit mechanics, marketplace, evaluators, repayment, auto-liquidation, cryptographic proofs, token economics, security model, and implementation roadmap.',
        file: '/kojiru-agent-credit-protocol-whitepaper.pdf',
        pages: 'Technical',
      },
      {
        name: 'Agent Credit: One-Pager',
        version: 'Executive summary',
        description: 'Single-page overview of the Agent Credit Protocol: the problem, the thesis, the mechanism, and the numbers. Ideal for investor intros and quick partner context.',
        file: '/kojiru-agent-credit-one-pager.pdf',
        pages: 'Executive',
      },
      {
        name: 'The Agent Credit High School Guide',
        version: 'Plain-English explainer',
        description: 'Agent Credit explained without jargon: designed for newcomers to DeFi, AI agents, and machine-to-machine credit. Reads in ~15 minutes.',
        file: '/kojiru-high-school-guide.pdf',
        pages: 'Beginner',
      },
    ],
  },
  {
    id: 'audits',
    title: 'Independent Reviews',
    description: 'Tooling-verified analysis of specific Kojiru contracts. We list only what has actually been run, scoped to the contracts where it has actually been run: nothing more.',
    icon: Shield,
    accent: 'from-emerald-500/10 to-teal-500/10',
    border: 'border-emerald-500/20',
    iconTint: 'text-emerald-400',
    items: [
      {
        name: 'Static Analysis: KojiruStakingV2',
        version: 'a leading static-analysis tool · clean (no high findings)',
        description: 'Static analysis run via a leading static-analysis tool against the KojiruStakingV2 contract that is live on Base mainnet. Covers re-entrancy, access control, integer arithmetic, and common Solidity pitfalls. Scoped to staking only: other Kojiru contracts have not been static-analyzed and are not claimed to be.',
        file: '/kojiru-security-audit-slither.pdf',
        pages: 'Audit · KojiruStakingV2 only',
      },
      {
        name: 'Symbolic Verification: KojiruACSHook',
        version: 'Symbolic execution · slash invariants proven',
        description: 'An open-source symbolic-execution tool was run against the KojiruACSHook contract before its Sepolia deploy. The slash routing invariants (recipient receives exactly the slashed amount, no value disappears) are symbolically proven. Spec lives in /contracts/formal-verification/halmos/ in the GitHub repo: no formal PDF report, just reproducible commands in the README.',
        file: 'https://github.com/faulknerwayne73-droid/kojiru-platform/tree/main/contracts/formal-verification/halmos',
        pages: 'GitHub · open-source spec',
      },
    ],
  },
  {
    id: 'thesis',
    title: 'Thesis & Position Papers',
    description: 'Public essays that build the case for the Recourse Layer category. Each piece is sourced against institutional data (a global consulting firm, a strategy firm, an investment bank, a major card network first-party surveys) and references the live on-chain primitives on Base mainnet.',
    icon: BookOpen,
    accent: 'from-amber-500/10 to-rose-500/10',
    border: 'border-amber-500/20',
    iconTint: 'text-amber-400',
    items: [
      {
        name: 'Unauthorized Agent Signatures: Blocked by Default at the Gate',
        version: 'The Binding Action Gate | Proposed design, not in service',
        description: 'A proposed design, not in service. Describes how legally binding agent actions (sign, accept terms, execute a contract) would be blocked by default and executed only against a human-signed EIP-712 consent receipt bound to the exact document hash. Covers the action classes, binding authority postures, the receipt format, four scenarios, what the gate does not claim, legal notices, and open questions. Kojiru accepts no receipts, checks, or payments for the gate.',
        file: '/downloads/kojiru-binding-action-gate-brief.pdf?v=7',
        pages: 'Brief | Proposed | Not in service',
      },
      {
        name: 'Open Consent Receipt Specification',
        version: 'Draft 0.1 | Published October 12, 2026 | Free open format',
        description: 'A free, open format for a person to authorize one agent to take one legally binding action on one exact document. Operators run signing, verification, storage, and records on their own systems. Kojiru only publishes the text and operates no service for it. Web version at kojiru.com/specs/consent-receipt.',
        file: '/downloads/kojiru-open-consent-receipt-spec-draft-0.1.pdf',
        pages: 'Spec | Draft 0.1 | Published only',
      },
      {
        name: 'Claim Records and Purchase Intent (Draft 0.4 Proposal)',
        version: 'Proposal for comment | October 21, 2026 | Not part of the specification',
        description: 'A proposed addition to the Open Consent Receipt Specification for what happens after an agent purchase: one ID and one case for every refund or complaint, a delivery receipt for every request and safe retries, a contest step decided by a person, visible deadlines, signed intent checks including refusals, and an optional consumer-signed record of what was asked for. Nothing built. Kojiru only publishes the text.',
        file: '/downloads/kojiru-claim-records-draft-0.4-proposal.pdf',
        pages: 'Proposal | 18 pages | For comment',
      },
      {
        name: 'Who Said Yes? (Article)',
        version: 'October 12, 2026 | Open Consent Receipt Specification',
        description: 'A short article on the Open Consent Receipt Specification: the gap it fills, how it works, its unique selling proposition, benefits, value, who needs it, and why it works alongside governance and policy engines rather than against them.',
        file: '/downloads/kojiru-open-consent-receipt-linkedin-article.pdf?v=2',
        pages: 'Article | 4 pages',
      },
      {
        name: 'The First Serious Failure Arrived',
        version: 'Published October 9, 2026 · 1,480 words · Short Response',
        description: 'A short response to the October 8, 2026 Korean banks AI-agent breach, the first known AI-agent breach of the financial sector. Three specific reads: (1) the attack path (third-party channels) is exactly what pillar-four primitives are designed to catch, (2) the MAS and OSFI "the vendor model is your risk" framing structurally pre-aligns with a non-custodial pillar-four primitive, and (3) the industry convergence timeline is compressing, not expanding. Companion to "The New Agentic Commerce Frontier: Allocating Liability".',
        file: '/downloads/kojiru-first-serious-failure-arrived.pdf',
        pages: 'Essay · Response · Pillar Four',
      },
      {
        name: 'The New Agentic Commerce Frontier: Allocating Liability',
        version: 'Published October 8, 2026 · 3,100 words · Position Paper',
        description: 'A response to the October 7, 2026 editorial from the paper of record for the US payments industry that enumerated four pillars of interoperable agentic trust standards. Three pillars have declared claimants; the fourth, allocating liability, is structurally unclaimed. This paper describes what the fourth pillar looks like at the specification layer, maps six composition surfaces (networks, issuers, processors, acquirers, insurance underwriters, regulators), and references the live Base mainnet deployment (KojiruACSOracle at 0x93BF…3B74e, KojiruACSHook at 0x05F2…0874) and the pledge_address proposal formally acknowledged by the global EMV standards body on October 8, 2026.',
        file: '/downloads/kojiru-agentic-commerce-frontier-allocating-liability.pdf',
        pages: 'Essay · Position · Pillar Four',
      },
      {
        name: 'Fourth Pillar Brief (2-slide)',
        version: 'Published October 8, 2026 · Landscape · BD attachment',
        description: 'A two-slide institutional brief distilling the four-pillar framework, the Kojiru primitive map (mandate / record / slash), the adopt-verbatim institutional vocabulary table, and the triple-verified consumer signal (three independent 2026 surveys from three jurisdictions). Designed as the attachment version of the position paper for merchant, insurer, network, and regulator conversations.',
        file: '/downloads/kojiru-fourth-pillar-brief.pdf',
        pages: 'Brief · 2 slides · Landscape',
      },
      {
        name: 'Agentic Commerce Recourse, Liabilities, and Self-Regulation',
        version: 'Published October 6, 2026 · 3,610 words',
        description: 'A response to the question of who is accountable for the choice when an agent moves the money. Builds the case through the cost of inaction (institutional TAM projections and the major card network first-party trust-gap survey), who stands to benefit across seven stakeholder classes, and what a cryptographically-bounded recourse primitive signals to regulators, legislatures, markets, and consumers. References the canonical USDC-bound Pair A on Base mainnet (KojiruACSHook at 0x05F2…0874).',
        file: '/downloads/kojiru-agentic-commerce-recourse-liabilities-self-regulation.pdf',
        pages: 'Essay · Position',
      },
    ],
  },
];

export default function Resources() {
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="resources-page-root">
      <Navbar />
      <main className="pt-24 pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-16"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <FileText className="w-5 h-5" />
              </div>
              <span className="text-xs font-mono text-blue-400 tracking-widest uppercase">Kojiru Resources</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">Research, Audits & Documentation</h1>
            <p className="text-lg text-gray-400 max-w-2xl leading-relaxed">
              Everything you need to understand, evaluate, and integrate the Agent Credit Protocol. All documents are public, free to share, and updated as the protocol evolves.
            </p>
          </motion.div>

          {/* Categories */}
          <div className="space-y-14">
            {CATEGORIES.map((category, catIdx) => {
              const CategoryIcon = category.icon;
              return (
                <motion.section
                  key={category.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 + catIdx * 0.05 }}
                  data-testid={`resource-category-${category.id}`}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${category.accent} border ${category.border} flex items-center justify-center`}>
                      <CategoryIcon className={`w-5 h-5 ${category.iconTint}`} />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">{category.title}</h2>
                      <p className="text-sm text-gray-500 mt-0.5">{category.description}</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    {category.items.map((item) => (
                      <Card
                        key={item.file}
                        className={`bg-white/[0.02] border-white/5 hover:border-white/20 transition-colors group`}
                        data-testid={`resource-item-${item.file.replace(/\W/g, '-')}`}
                      >
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <h3 className="font-semibold text-base leading-snug">{item.name}</h3>
                            <span className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider ${category.iconTint} bg-white/5`}>
                              {item.pages}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mb-3 font-mono">{item.version}</p>
                          <p className="text-sm text-gray-400 leading-relaxed mb-5 line-clamp-4">{item.description}</p>
                          <a
                            href={item.file}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm font-medium text-white hover:text-blue-400 transition-colors group-hover:gap-3"
                            data-testid={`download-${item.file.replace(/\W/g, '-')}`}
                          >
                            <Download className="w-4 h-4" />
                            Open PDF
                            <ArrowUpRight className="w-3.5 h-3.5 opacity-60" />
                          </a>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </motion.section>
              );
            })}
          </div>

          {/* Footer note */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-20 p-6 rounded-xl bg-white/[0.02] border border-white/5 text-sm text-gray-400 leading-relaxed"
          >
            <p>
              Looking for something not listed here? The full research and investor material library is available on request: {' '}
              <a href="mailto:wayne@kojiru.com" className="text-blue-400 hover:text-blue-300 underline underline-offset-2" data-testid="resources-contact-link">
                wayne@kojiru.com
              </a>
              . For technical integration docs and SDK references, visit the{' '}
              <a href="/developers" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">
                Developers
              </a>{' '}
              page.
            </p>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
